/**
 * My Brand audience-segment tokens (`core.raw_table_view`.`audience:audience_segment`).
 *
 * Prospecting / Retargeting match live BQ values. Unclassified is a residual UI
 * token; `toBqAudienceSegment` is the canonical conversion to leftover BQ values.
 * Product copy lives in the frontend i18n files — this module owns wire values only.
 */

export const AUDIENCE_SEGMENT_PROSPECTING = 'new audience';
export const AUDIENCE_SEGMENT_RETARGETING = 'existing customers';
export const AUDIENCE_SEGMENT_UNCLASSIFIED = 'unclassified';

export const AUDIENCE_SEGMENT_UNCLASSIFIED_BQ_VALUES = [
  'engaged audience',
  'all',
  'unknown',
] as const;

export const AUDIENCE_SEGMENT_VALUES = [
  AUDIENCE_SEGMENT_PROSPECTING,
  AUDIENCE_SEGMENT_RETARGETING,
  AUDIENCE_SEGMENT_UNCLASSIFIED,
] as const;

export type AudienceSegmentValue = (typeof AUDIENCE_SEGMENT_VALUES)[number];

const LEGACY_AUDIENCE_SEGMENT_VALUES: Readonly<Record<string, string>> = {
  Prospecting: AUDIENCE_SEGMENT_PROSPECTING,
  Retargeting: AUDIENCE_SEGMENT_RETARGETING,
};

const UNCLASSIFIED_BQ_VALUE_SET = new Set<string>(AUDIENCE_SEGMENT_UNCLASSIFIED_BQ_VALUES);
const AUDIENCE_UI_VALUE_SET = new Set<string>(AUDIENCE_SEGMENT_VALUES);

function uniquePreserveOrder(values: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of values) {
    if (seen.has(v)) continue;
    seen.add(v);
    out.push(v);
  }
  return out;
}

function isAllAudienceUiOptions(values: string[]): boolean {
  if (values.length !== AUDIENCE_SEGMENT_VALUES.length) return false;
  return values.every((v) => AUDIENCE_UI_VALUE_SET.has(v))
    && new Set(values).size === AUDIENCE_SEGMENT_VALUES.length;
}

/** Rewrite persisted/URL audience values onto the current UI tokens. */
export function remapAudienceSegmentValues(values: string[]): string[] {
  return uniquePreserveOrder(
    values
      .map((v) => v.trim())
      .filter((v) => v !== '')
      .map((v) => {
        if (LEGACY_AUDIENCE_SEGMENT_VALUES[v]) return LEGACY_AUDIENCE_SEGMENT_VALUES[v];
        if (UNCLASSIFIED_BQ_VALUE_SET.has(v)) return AUDIENCE_SEGMENT_UNCLASSIFIED;
        return v;
      }),
  );
}

/** Expand UI tokens to BQ values for query payloads. Empty / all-three → All (`[]`). */
export function expandAudienceSegmentForQuery(uiValues: string[]): string[] {
  const uniqueUi = uniquePreserveOrder(uiValues.map((v) => v.trim()).filter((v) => v !== ''));
  if (uniqueUi.length === 0 || isAllAudienceUiOptions(uniqueUi)) return [];
  return uniqueUi.flatMap((v) =>
    v === AUDIENCE_SEGMENT_UNCLASSIFIED ? [...AUDIENCE_SEGMENT_UNCLASSIFIED_BQ_VALUES] : [v],
  );
}

/**
 * Canonical BQ audience-segment list.
 * Empty / all-three UI tokens => All (`[]`).
 */
export function toBqAudienceSegment(values: string[]): string[] {
  return expandAudienceSegmentForQuery(remapAudienceSegmentValues(values));
}
